import { isHttpError } from "~/utils/httpClient";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { formatCurrency } from "@sellar-io/common";
import { Button, Icon } from "@sellar-io/components";
import { StatePrompts } from "../../../components/statePrompts/statePrompts";
import {
  SkeletonContainer,
  SkeletonAvatar,
  SkeletonText,
} from "../../../components/skeleton/Skeleton";
import { useOrderLinkFlowOrder } from "../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { useMenuDrawerContext } from "../../../components/menu/MenuDrawerContextProvider.shared";
import * as ROUTES from "../../../constants/routes";
import { RestrictedAccessScreen } from "./components/RestrictedAccessScreen";
import { useEvents } from "../../../hooks";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";

type OrderSummaryState =
  | "loading"
  | "missing_token"
  | "invalid_token"
  | "expired_token"
  | "order_unavailable"
  | "order_buyer_has_business_users"
  | "success";

const deriveState = ({
  token,
  orderLoading,
  orderError,
  orderSuccess,
  orderBuyerHasBusinessUsers,
}: {
  token: string | null;
  orderLoading: boolean;
  orderError: unknown;
  orderBuyerHasBusinessUsers: boolean;
  orderSuccess: boolean;
}): OrderSummaryState => {
  // TODO: show error if order cancelled or not correct delivery type

  if (!token) return "missing_token";
  if (orderLoading) return "loading";

  if (orderError) {
    if (isHttpError(orderError)) {
      const status = orderError.response?.status;
      const message: string =
        (orderError.response?.data as { message?: string } | undefined)
          ?.message ?? "";

      if (status === 401) return "invalid_token";
      if (status === 400 && message.toLowerCase().includes("expired"))
        return "expired_token";
    }
    return "order_unavailable";
  }

  if (orderBuyerHasBusinessUsers) {
    return "order_buyer_has_business_users";
  }

  if (orderSuccess) return "success";

  return "loading";
};

const OrderSummarySkeleton = () => (
  <div className="flex min-h-screen flex-col items-center bg-neutral-100 px-5 py-10">
    <SkeletonContainer className="w-full max-w-md flex-col gap-4">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <SkeletonAvatar className="h-14 w-14" />
          <SkeletonText className="h-5 w-2/3" />
          <SkeletonText className="h-4 w-1/2" />
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <div className="flex justify-between">
            <SkeletonText className="h-3 w-24" />
            <SkeletonText className="h-3 w-20" />
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex justify-between">
              <SkeletonText className="h-4 w-2/3" />
              <SkeletonText className="h-4 w-16" />
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg border border-neutral-100 p-3">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <SkeletonText className="h-4 w-16" />
              <SkeletonText className="h-4 w-16" />
            </div>
            <div className="flex justify-between">
              <SkeletonText className="h-4 w-20" />
              <SkeletonText className="h-4 w-16" />
            </div>
            <div className="flex justify-between">
              <SkeletonText className="h-5 w-24" />
              <SkeletonText className="h-5 w-20" />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <SkeletonText className="h-10 w-full rounded-lg" />
        </div>
      </div>

      <SkeletonText className="h-12 w-full rounded-2xl" />
      <SkeletonText className="h-12 w-full rounded-2xl" />
    </SkeletonContainer>
  </div>
);

const formatBuyerLocation = (
  name: string,
  postalCode: string | null,
): string => {
  const parts = [name, postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
};

export const OrderSummary = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const action = searchParams.get("action");
  const navigate = useNavigate();

  const orderQuery = useOrderLinkFlowOrder(token);
  const { setDisplayTypeOverride } = useMenuDrawerContext();
  const { track } = useEvents();

  const buyerHasBusinessUser = orderQuery.data?.retailerBusinessUsers
    ? orderQuery.data.retailerBusinessUsers.length > 0
    : false;

  const state = deriveState({
    token,
    orderLoading: orderQuery.isLoading,
    orderError: orderQuery.error,
    orderSuccess: orderQuery.isSuccess,
    orderBuyerHasBusinessUsers: buyerHasBusinessUser,
  });

  useEffect(() => {
    setDisplayTypeOverride("hidden");
    return () => setDisplayTypeOverride(undefined); // restore on unmount
  }, [setDisplayTypeOverride]);

  useEffect(() => {
    if (state !== "success" || !orderQuery.data) return;
    track("Order Summary Viewed", {
      ...getSellarWebsiteUtms(),
      orderReference: orderQuery.data.orderReference,
    });
  }, [state, orderQuery.data, track]);

  if (state === "loading") {
    return <OrderSummarySkeleton />;
  }

  if (state === "missing_token" || state === "invalid_token") {
    return (
      <StatePrompts
        title="Invalid link"
        description="This link is not valid. Please check your email for the correct link."
      />
    );
  }

  if (state === "expired_token") {
    return (
      <StatePrompts
        title="Link expired"
        description="This link has expired. Please contact your supplier for a new one."
      />
    );
  }

  if (state === "order_unavailable") {
    return (
      <StatePrompts
        title="Order not available"
        description="This order was not found."
      />
    );
  }

  if (state === "order_buyer_has_business_users") {
    return <RestrictedAccessScreen order={orderQuery.data!} />;
  }

  const order = orderQuery.data;

  if (!order) return null;

  return (
    <div className="flex min-h-screen flex-col items-center bg-neutral-100 px-5 py-10">
      <div className="w-full max-w-120">
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center gap-2">
            {order.supplierLogoUrl ? (
              <img
                src={order.supplierLogoUrl}
                alt={order.supplierName}
                className="h-14 w-14 rounded-full object-contain"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                <Icon icon="STOREFRONT_OUTLINE" size={1.5} />
              </div>
            )}
            <h1 className="text-center text-2xl font-semibold text-zinc-800">
              Your order with {order.supplierName}
            </h1>

            <p className="text-center text-sm text-zinc-600">
              {formatBuyerLocation(
                order.retailerName,
                order.retailerPostalCode,
              )}
            </p>
          </div>

          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between text-xs font-semibold tracking-wide text-zinc-500 uppercase">
              <span>{order.supplierName}</span>
              <span>ORDER #{order.orderReference}</span>
            </div>

            <div className="flex flex-col gap-2">
              {order.lineItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-zinc-800">
                    <span className="font-medium">{item.quantity}x</span>
                    {"  "}
                    {item.productName}
                  </span>
                  <span className="ml-4 shrink-0 text-zinc-800">
                    {formatCurrency(item.lineTotalPence)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-zinc-300 bg-zinc-100 p-3">
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex justify-between font-normal text-zinc-800">
                <span>Subtotal</span>
                <span>{formatCurrency(order.subtotalPence)}</span>
              </div>
              {order.discountAmountPence !== 0 && (
                <div className="flex justify-between font-normal text-zinc-800">
                  <span>Discount</span>
                  <span>-{formatCurrency(order.discountAmountPence)}</span>
                </div>
              )}
              {order.vatAmountPence !== null && (
                <div className="flex justify-between font-normal text-zinc-800">
                  <span>VAT ({order.vatPercentage}%)</span>
                  <span>{formatCurrency(order.vatAmountPence)}</span>
                </div>
              )}
              <div className="mt-1 flex justify-between border-t border-neutral-100 pt-2 font-semibold text-zinc-800">
                <span>Order total</span>
                <span>{formatCurrency(order.totalPence)}</span>
              </div>
            </div>
          </div>

          {order.estimatedDeliveryDate && (
            <div className="mt-4 flex items-center justify-between rounded-lg border border-indigo-300 bg-indigo-100 px-3 py-2.5 text-sm">
              <span className="flex items-center gap-2 text-indigo-800">
                <Icon icon="TRUCK_OUTLINE" size={0.9} />
                Estimated delivery date
              </span>
              <span className="font-medium text-indigo-800">
                {dayjs(order.estimatedDeliveryDate).format("DD/MM/YYYY")}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {action === "edit" ? (
            <>
              <Button
                className="w-full"
                onClick={() => {
                  track("Order Summary Edit Or Add Clicked", {
                    ...getSellarWebsiteUtms(),
                  });
                  navigate({
                    pathname: ROUTES.ORDER_SUMMARY_EDIT_ADD_SELECTION,
                    search: searchParams.toString(),
                  });
                }}
              >
                EDIT OR ADD TO ORDER
                <Icon icon="CHEVRON_RIGHT" size={0.9} />
              </Button>
              <Button
                color="secondary"
                className="w-full"
                onClick={() => {
                  track("Order Summary Delivery Preferences Clicked", {
                    ...getSellarWebsiteUtms(),
                  });
                  navigate({
                    pathname: ROUTES.ORDER_SUMMARY_DELIVERY_PREFERENCES,
                    search: searchParams.toString(),
                  });
                }}
              >
                SET DELIVERY PREFERENCES
                <Icon icon="CHEVRON_RIGHT" size={0.9} />
              </Button>
            </>
          ) : (
            <>
              <Button
                className="w-full"
                onClick={() => {
                  track("Order Summary Delivery Preferences Clicked", {
                    ...getSellarWebsiteUtms(),
                  });
                  navigate({
                    pathname: ROUTES.ORDER_SUMMARY_DELIVERY_PREFERENCES,
                    search: searchParams.toString(),
                  });
                }}
              >
                SET DELIVERY PREFERENCES
                <Icon icon="CHEVRON_RIGHT" size={0.9} />
              </Button>
              <Button
                color="secondary"
                className="w-full"
                onClick={() => {
                  track("Order Summary Edit Or Add Clicked", {
                    ...getSellarWebsiteUtms(),
                  });
                  navigate({
                    pathname: ROUTES.ORDER_SUMMARY_EDIT_ADD_SELECTION,
                    search: searchParams.toString(),
                  });
                }}
              >
                EDIT OR ADD TO ORDER
                <Icon icon="CHEVRON_RIGHT" size={0.9} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
